import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig, // Updated type for request interceptors
} from "axios";

import { refreshIdToken } from "../contexts/aws-cognito-context";

import {
  getPageInstanceId,
  TAB_AWARE_SOCKET_VERSION,
} from "./pageInstance";

// 1. VITE CHANGE: Access env vars via import.meta.env
const baseURL = import.meta.env.VITE_API_BASE_URL;
const basePath = import.meta.env.VITE_API_BASE_PATH;

const axiosServices: AxiosInstance = axios.create({
  baseURL: `${baseURL}/${basePath}`,
});

const isTabAwareSocketRequest = (url?: string) => {
  const path = (url || "").split("?")[0].replace(/\/+$/, "");
  return path.endsWith("/chat") || path.endsWith("/chatstreaming");
};

const handleUnauthorized = () => {
  const logoutEvent = new CustomEvent("auth:logout");
  window.dispatchEvent(logoutEvent);
};

// 2. Request interceptor
axiosServices.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("tmaWebAppServiceToken") || "";
    const tabId = getPageInstanceId();

    // In modern Axios, headers are always defined, but good to be safe
    if (config.headers) {
      config.headers.Authorization = token;
      config.headers["x-app-tab-id"] = tabId;
      if (isTabAwareSocketRequest(config.url)) {
        config.headers["x-app-socket-version"] = TAB_AWARE_SOCKET_VERSION;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 3. Response interceptor
axiosServices.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Check if the error is a 401 and the _retry property is not already set
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true; // Mark the request as retried

      try {
        const newToken = await refreshIdToken(); // Refresh the token

        if (!newToken) {
          return Promise.reject(error); // If refresh fails, reject
        }

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = newToken; // Set the new token
        }

        // Retry the original request
        return axiosServices(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        handleUnauthorized();
        // Ideally reject here so the calling component knows it failed
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error); // If not a 401 or something else goes wrong
  }
);

export default axiosServices;
